/**
 * Addresses are stored as flat, top-level user fields rather than nested objects, and the farm and
 * mailing sets do not share a naming convention (`town`/`mailing_city`, `postal_code`/`mailing_zip`).
 * These maps declare the target -> source pairing once so forms can mirror one address onto the other.
 */
export const FARM_TO_MAILING_ADDRESS_FIELDS = {
    mailing_address1: 'address1',
    mailing_address2: 'address2',
    mailing_city: 'town',
    mailing_state: 'state',
    mailing_zip: 'postal_code',
};

// Only the legacy producer form collects a country pair; the profile edit configs have no country field.
export const FARM_TO_MAILING_ADDRESS_FIELDS_WITH_COUNTRY = {
    ...FARM_TO_MAILING_ADDRESS_FIELDS,
    mailing_country: 'country',
};

// Searchable selects hold their value as a { name, value } option object rather than a bare string.
const plainValue = (value) => (value && typeof value === 'object' && 'value' in value ? value.value : value);

const isBlank = (value) => {
    const plain = plainValue(value);
    return plain == null || plain === '';
};

/**
 * True when any field on either side of the map holds a value. Lets a form tell "nothing has loaded
 * yet" apart from "loaded, but the addresses are genuinely empty".
 * @param {object} values form values keyed by field name
 * @param {object} fieldMap { [targetField]: sourceField }
 */
export const hasAnyValue = (values, fieldMap) => Object.entries(fieldMap).some(([target, source]) => !isBlank(values?.[target]) || !isBlank(values?.[source]));

/**
 * True when every target field already holds its source field's value, and at least one of them is
 * filled in. Used to tick a "same as" checkbox on load without persisting the flag.
 * @param {object} values form values keyed by field name
 * @param {object} fieldMap { [targetField]: sourceField }
 */
export const matchesSource = (values, fieldMap) => {
    if (!hasAnyValue(values, fieldMap)) return false;
    return Object.entries(fieldMap).every(([target, source]) => {
        const targetValue = plainValue(values?.[target]);
        const sourceValue = plainValue(values?.[source]);
        if (isBlank(targetValue) && isBlank(sourceValue)) return true;
        return targetValue === sourceValue;
    });
};
